import React, { FC, useContext } from 'react';
import {
  CoverageOptionsResponse,
  CoveredPartyPayloadType,
} from '@clearcover/core-app-types';
import { getIn, useFormikContext } from 'formik';
import { DriverReportTable } from './DriverReportTable';
import classNames from 'classnames';
import styles from './styles.module.css';
import {
  Asset,
  CheckmarkIcon,
  CloseIcon,
  ModalError,
  ModalLoading,
} from '@clearcover/atlas';
import { FormInput } from 'Shared/components';
import { DriverType } from '@clearcover/core-app-types';
import { useNewApi } from 'Shared/services/Api';
import { PolicyContext } from 'Policies/utils/PolicyContext';
import { PolicyContextType } from 'Policies/utils/PolicyContext/PolicyContext';

type DriverPriorInsuranceProps = {
  priorCarrier: DriverType['priorCarrier'];
};

const showIcon = (challenge?: string) => {
  return (
    <div className={styles.ratingIcon}>
      <Asset
        icon={challenge !== 'removed' ? CheckmarkIcon : CloseIcon}
        description={'Rated'}
        height={20}
        width={20}
        className={
          challenge !== 'removed' ? styles.checkmarkIcon : styles.closeIcon
        }
      />
    </div>
  );
};

export const DriverPriorInsurance: FC<DriverPriorInsuranceProps> = ({
  priorCarrier,
}) => {
  const {
    details: { filingIdentifier },
  } = useContext(PolicyContext) as PolicyContextType;

  const { handleBlur, handleChange, setFieldValue, values, touched, errors } =
    useFormikContext<CoveredPartyPayloadType>();

  const [{ data: coverageOptions, loading, error }] =
    useNewApi<CoverageOptionsResponse>(
      { url: `/options/coverages?identifier=${filingIdentifier}` },
      true // useCache: prevents unnecessary re-requests for static coverage options
    );

  if (loading) {
    return <ModalLoading data-testid="coverage-modal-loading" />;
  }

  if (error) {
    return (
      <ModalError
        data-testid="edit-coverages-error"
        errorMessage={error?.message ?? ''}
        componentStack={error?.stack ?? ''}
      />
    );
  }

  return (
    <>
      <h3>Prior Insurance</h3>
      <DriverReportTable
        data-testid="prior-insurance-report-table"
        headers={[
          'RATED',
          'SOURCE',
          'CURRENT CARRIER',
          'DURATION',
          'BI LIMITS',
          '',
        ]}
        tableRows={[
          {
            data: [
              <div
                className={classNames(styles.reportData, {
                  [styles.small]: true,
                })}
              >
                {showIcon(getIn(values, `reports.priorCarrier.challenge`))}
              </div>,
              <div
                className={classNames(styles.reportData, {
                  [styles.small]: true,
                })}
              >
                <span>{priorCarrier.source}</span>
              </div>,
              <div className={styles.reportData}>
                <FormInput
                  variant="text"
                  name={'reports.priorCarrier.carrier'}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    priorCarrier.source !== 'User Reported' &&
                      setFieldValue('reports.priorCarrier.challenge', 'edited');
                    handleChange(e);
                  }}
                  onBlur={handleBlur}
                  value={values.reports.priorCarrier.carrier}
                  errorMessage={
                    getIn(touched, 'reports.priorCarrier.carrier') &&
                    getIn(errors, 'reports.priorCarrier.carrier')
                  }
                  label="&nbsp;"
                  data-testid={'reports.priorCarrier.carrier'}
                />
              </div>,
              <div className={styles.reportData}>
                {values.reports.priorCarrier.isCurrentlyInsured ? (
                  <FormInput
                    variant="text"
                    label="currently insured months"
                    name={
                      'reports.priorCarrier.duration.currentlyInsuredMonths'
                    }
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      priorCarrier.source !== 'User Reported' &&
                        setFieldValue(
                          'reports.priorCarrier.challenge',
                          'edited'
                        );
                      handleChange(e);
                    }}
                    onBlur={handleBlur}
                    value={
                      values.reports.priorCarrier.duration
                        .currentlyInsuredMonths
                    }
                    errorMessage={
                      getIn(
                        touched,
                        'reports.priorCarrier.duration.currentlyInsuredMonths'
                      ) &&
                      getIn(
                        errors,
                        'reports.priorCarrier.duration.currentlyInsuredMonths'
                      )
                    }
                    data-testid={
                      'reports.priorCarrier.duration.currentlyInsuredMonths'
                    }
                  />
                ) : (
                  <FormInput
                    variant="text"
                    label="days since last insured"
                    name={'reports.priorCarrier.duration.daysSinceLastInsured'}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      priorCarrier.source !== 'User Reported' &&
                        setFieldValue(
                          'reports.priorCarrier.challenge',
                          'edited'
                        );
                      handleChange(e);
                    }}
                    onBlur={handleBlur}
                    errorMessage={
                      getIn(
                        touched,
                        'reports.priorCarrier.duration.daysSinceLastInsured'
                      ) &&
                      getIn(
                        errors,
                        'reports.priorCarrier.duration.daysSinceLastInsured'
                      )
                    }
                    value={
                      values.reports.priorCarrier.duration.daysSinceLastInsured
                    }
                    data-testid={
                      'reports.priorCarrier.duration.daysSinceLastInsured'
                    }
                  />
                )}
              </div>,
              <div className={styles.reportData}>
                <FormInput
                  variant="select"
                  className={styles.dropdownMenu}
                  name={'reports.priorCarrier.bodyInjuryLimits'}
                  onChange={(value: string) => {
                    priorCarrier.source !== 'User Reported' &&
                      setFieldValue('reports.priorCarrier.challenge', 'edited');
                    setFieldValue(
                      'reports.priorCarrier.bodyInjuryLimits',
                      value
                    );
                  }}
                  value={values.reports.priorCarrier.bodyInjuryLimits}
                  label=""
                  data-testid={'reports.priorCarrier.bodyInjuryLimits'}
                  errorMessage={
                    getIn(
                      values,
                      'touched.reports.priorCarrier.bodyInjuryLimits'
                    ) &&
                    getIn(
                      values,
                      'errors.reports.priorCarrier.bodyInjuryLimits'
                    )
                  }
                  options={
                    coverageOptions?.policy?.coverages?.find(
                      (coverage) => coverage.identifier === 'BI'
                    )?.coverageOptions ?? {}
                  }
                />
              </div>,
            ],
          },
        ]}
      />
    </>
  );
};

export default DriverPriorInsurance;
